import { graphql, Link } from "gatsby";
import React from "react";
import styled from "styled-components";
import Layout from "../components/layout";
import Seo from "../components/seo";
import { SectionWrapper, NoData } from "../components/common";

const BlogPageWrapper = styled(SectionWrapper)`
  padding: 1em 3em 5em;
  max-width: 100%;

  @media (min-width: 1200px) {
    padding: 1em 4em 5em;
  }

  @media (min-width: 1500px) {
    padding: 1em 6em 5em;
  }

  @media (max-width: 768px) {
    padding: 1em 1.5em 4em;
  }
`;

const PageTitle = styled.h2`
  background: ${({ theme }) => theme.colors.secondaryGradient};
  font-size: 48px;
  padding: 15px 0;
  line-height: 0.9em;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Tagline = styled.p`
  max-width: 820px;
  color: #cfcfcf;
  margin-bottom: 2.5em;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 920px;
`;

const Post = styled.article`
  padding: 1.8em 0;
  border-top: 1px solid #2a2a2a;

  &:last-child {
    border-bottom: 1px solid #2a2a2a;
  }
`;

const PostTitle = styled.h3`
  font-size: 1.6em;
  margin: 0 0 0.4em;
  font-weight: 700;

  a {
    color: inherit;
    text-decoration: none;
  }

  a:hover {
    background: ${({ theme }) => theme.colors.secondaryGradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const PostMeta = styled.p`
  font-size: 0.9em !important;
  color: #888;
  margin: 0 0 1em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PostExcerpt = styled.p`
  font-size: 1.05em !important;
  line-height: 1.6em !important;
  color: #cfcfcf;
  margin: 0 0 1em;
`;

const ReadMore = styled(Link)`
  color: #f2994a;
  text-decoration: none;
  font-size: 0.95em;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

function BlogPage({ data }) {
  const blogs = data.blogs.nodes;

  return (
    <Layout>
      <Seo
        title="Blog"
        description="Notes on backend systems, payments infrastructure, fraud detection, and the occasional rant about Postgres."
      />
      <BlogPageWrapper>
        <PageTitle>Blog</PageTitle>
        <Tagline>
          Notes on backend systems, payments infrastructure, fraud detection,
          and the occasional rant about Postgres.
        </Tagline>

        {blogs.length === 0 ? (
          <NoData>
            Please check back later...{" "}
            <span role="img" aria-label="smiley">
              &#128522;
            </span>
          </NoData>
        ) : (
          <PostList>
            {blogs.map(({ frontmatter, excerpt, id }) => {
              const href = `/blogs/${frontmatter.slug}`;
              return (
                <Post key={id}>
                  <PostTitle>
                    <Link to={href}>{frontmatter.title}</Link>
                  </PostTitle>
                  {frontmatter.formattedDate && (
                    <PostMeta>{frontmatter.formattedDate}</PostMeta>
                  )}
                  {excerpt && <PostExcerpt>{excerpt}</PostExcerpt>}
                  <ReadMore to={href}>Read more →</ReadMore>
                </Post>
              );
            })}
          </PostList>
        )}
      </BlogPageWrapper>
    </Layout>
  );
}

export default BlogPage;

export const query = graphql`
  {
    blogs: allMarkdownRemark(
      filter: { frontmatter: { type: { eq: "blogs" } } }
      sort: { fields: frontmatter___postedDate, order: DESC }
    ) {
      nodes {
        frontmatter {
          slug
          title
          type
          formattedDate: postedDate(formatString: "MMM D, YYYY")
        }
        excerpt(pruneLength: 220)
        id
      }
    }
  }
`;
